import { useLazyQuery } from "@apollo/client/react";
import { debounce } from "es-toolkit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  AccountByUsernameDocument,
  AccountsOrderBy,
  UserSearchDocument,
} from "@/graphql/codegen/graphql";
import {
  concatUserResponses,
  isAbortError,
  isDesoPublicKey,
} from "@/utils/helpers";
import type { PartialAccount } from "@/utils/types";

enum SEARCH_ENTITY {
  USERNAME = "Username",
  PUB_KEY = "User Public Key",
}

const validateInput = (input: string): string[] => {
  const matchedTypes: string[] = [];

  // Username: [a-zA-Z0-9_] from 1 to 26 characters
  if (/^[a-zA-Z0-9_]{1,26}$/.test(input))
    matchedTypes.push(SEARCH_ENTITY.USERNAME);

  // User public key: Starts with 'BC', length 55, base58 characters
  if (isDesoPublicKey(input)) matchedTypes.push(SEARCH_ENTITY.PUB_KEY);

  return matchedTypes;
};

export type PartialAccountWithParams = PartialAccount & {
  isUnknownDesoUser?: boolean;
};

interface SearchResults {
  accounts: Array<PartialAccountWithParams>;
}

const getEmptySearchResults = (): SearchResults => {
  return {
    accounts: [],
  };
};

export const useUserSearch = (
  searchValue: string,
): {
  searchResults: SearchResults;
  loading: boolean;
  reset: () => void;
  search: (
    inputValue: string,
    abortController: AbortController,
  ) => Promise<void>;
  aborterRef: AbortController;
} => {
  const searchRequestIdRef = useRef(0);
  const [aborterRef, setAbortRef] = useState(new AbortController());

  const [fetchSearchResultsLazy, { loading: loadingSearchResults }] =
    useLazyQuery(UserSearchDocument);
  const [fetchAccountByUsername, { loading: loadingAccountByUserName }] =
    useLazyQuery(AccountByUsernameDocument);

  const [searchResults, setSearchResults] = useState<SearchResults>(
    getEmptySearchResults(),
  );

  const search = useCallback(
    async (inputValue = "", abortController: AbortController) => {
      const value = inputValue && inputValue.trim();
      const requestId = ++searchRequestIdRef.current;

      setSearchResults(getEmptySearchResults());

      if (!value) {
        return;
      }

      const searchTypes = validateInput(value);

      const includeAccounts =
        searchTypes.includes(SEARCH_ENTITY.USERNAME) ||
        searchTypes.includes(SEARCH_ENTITY.PUB_KEY);

      try {
        const fetchSearchResultsRequest = fetchSearchResultsLazy({
          variables: {
            includeAccounts,
            accountsFilter: {
              ...(searchTypes.includes(SEARCH_ENTITY.USERNAME)
                ? {
                    username: {
                      likeInsensitive: `${value}%`,
                    },
                  }
                : {
                    publicKey: {
                      equalTo: value,
                    },
                  }),
              isBlacklisted: {
                equalTo: false,
              },
            },
            orderBy: AccountsOrderBy.DesoLockedNanosDesc,
            first: 3,
          },
          context: {
            fetchOptions: {
              signal: abortController.signal,
            },
          },
        });

        const [{ data }, exactMatch] = await Promise.all([
          fetchSearchResultsRequest,
          searchTypes.includes(SEARCH_ENTITY.USERNAME)
            ? fetchAccountByUsername({
                variables: { username: value.trim() },
                context: {
                  fetchOptions: {
                    signal: abortController.signal,
                  },
                },
              })
            : Promise.resolve(null),
        ]);

        if (
          abortController.signal.aborted ||
          requestId !== searchRequestIdRef.current
        ) {
          return;
        }

        const allAccounts = concatUserResponses(
          !exactMatch?.data?.accountByUsername?.isBlacklisted
            ? exactMatch?.data?.accountByUsername
            : null,
          data?.accounts?.nodes,
        );

        setSearchResults({
          accounts:
            allAccounts.length === 0 && isDesoPublicKey(value)
              ? [{ publicKey: value, username: "", isUnknownDesoUser: true }]
              : allAccounts,
        });
      } catch (error) {
        if (
          isAbortError(error) ||
          abortController.signal.aborted ||
          requestId !== searchRequestIdRef.current
        ) {
          return;
        }

        throw error;
      }
    },
    [fetchAccountByUsername, fetchSearchResultsLazy],
  );

  const searchDebounced = useMemo(
    () =>
      debounce((v, a) => {
        void search(v, a).catch((error) => {
          console.error(error);
        });
      }, 600),
    [search],
  );

  useEffect(() => {
    aborterRef.abort();

    const newAbortController = new AbortController();
    setAbortRef(newAbortController);

    searchDebounced(searchValue, newAbortController);
  }, [searchValue]);

  return {
    loading: loadingSearchResults || loadingAccountByUserName,
    searchResults,
    reset: () => setSearchResults(getEmptySearchResults()),
    search,
    aborterRef,
  };
};
